import React from 'react';
import { format, parse } from 'date-fns';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import { careReasonText } from '../constants';
import {
    dateTime,
    remainingReviewKinds,
    warningExplanation,
    duration,
    rowExplanation,
    rowNotes,
    rowSeconds,
    rowTone,
    timeText,
    workDateText
} from '../presentation';
import { patientLabel } from '../rowSets';
import useCareActorName from '../useCareActorName';
import * as Style from '../style.module.scss';
import { CareMonthOverview, CareRow } from '../types';

const CHECK_LABELS: Record<string, string> = {
    minutes_coverage: 'Patient in minutes CSV',
    time: 'Care time',
    contact: 'Patient contact',
    distinct_time: 'Time counted only once',
    device: 'Device use',
    condition: 'Qualifying conditions',
    gap: 'Enrollment changes',
    termination_time: 'Care on the enrollment or membership end date',
    rpm_concurrency: 'RTM and RPM billing review',
    cross_org_rtm: 'No conflicting RTM billing',
    base_dependency: 'First code is billable'
};
const STATE_LABELS: Record<string, string> = {
    passed: 'Confirmed',
    failed: 'Not met',
    unknown: 'Needs confirmation',
    not_checked: 'Not checked here',
    not_applicable: 'Does not apply'
};
const DATE_LABELS: Record<string, string> = {
    program_enrollment_date: 'Joined program',
    program_disenrollment_date: 'Left program',
    member_activated_at: 'Membership started',
    member_discharged_at: 'Membership ended',
    member_cancelled_at: 'Membership cancelled',
    eligible_start: 'Care counted from',
    eligible_end: 'Care counted until'
};

const REVIEW_LABELS: Record<string, string> = {
    missing_from_minutes_csv: 'Missing CSV minutes',
    csv_program_not_enrolled: 'Missing program enrollment',
    termination_time_unverified: 'End-date care timing',
    same_program_reenrollment_requires_review: 'Enrollment change',
    rpm_concurrency_review_required: 'RTM and RPM billing',
    condition_review_required: 'Qualifying conditions'
};

const SOURCE_LABELS: Record<string, string> = {
    provider_csv: 'Provider CSV',
    reviewer_confirmation: 'Reviewer decision',
    not_assessed: 'Not checked',
    enabled_populated_hero_organizations:
        'Participating Hero organizations with records for this month'
};

const summaryText = (row: CareRow, locked: boolean, month: string, baseCode?: string): string => {
    if (!row.is_current)
        return locked
            ? 'The saved result for this locked month is unavailable.'
            : 'Select Calculate billable codes to update this result before billing.';
    return rowExplanation(row, month, baseCode);
};

const EvidencePanel: React.FC<{ row: CareRow; month: string; overview?: CareMonthOverview; baseCode?: string }> = ({
    row,
    month,
    overview,
    baseCode
}) => {
    // Use only facts supplied for this row's evaluated result. Do not turn missing values into zero.
    const facts = row.is_current ? row.current_evidence.facts : null;
    const { actorName } = useCareActorName(facts?.decision_events?.map((event) => event.actor) ?? []);
    const notes = rowNotes(row);
    const uncertain = facts?.uncertain_activity ?? [];
    const uncertainSeconds = uncertain.reduce((total, activity) => total + activity.seconds, 0);
    const outsideSeconds = Math.max(0, (facts?.dropped_seconds ?? 0) - uncertainSeconds);
    const otherReviews = remainingReviewKinds(row);
    const attention =
        row.is_current && row.explanation_available && row.status_label === 'Needs review'
            ? Object.entries(row.checks).filter(
                  ([, check]) => check.state === 'failed' || check.state === 'unknown'
              )
            : [];
    const receipts = Object.entries(overview?.run.imports ?? {}).filter(
        ([, receipt]) =>
            receipt && typeof receipt === 'object' && typeof receipt.import_id === 'number'
    );
    return (
        <div className={Style.drawerContent}>
            <header className={Style.dialogHeader}>
                <H role="h5" noDefaultMargin>
                    {patientLabel(row)}
                </H>
                <P size="small" noDefaultMargin>
                    {row.program} · CPT {row.code} ·{' '}
                    {format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy')}
                </P>
            </header>
            <div className={`${Style.evidenceOutcome} ${Style[`billing_${rowTone(row)}`]}`}>
                <P strong noDefaultMargin>
                    {!row.is_current
                        ? 'Needs update'
                        : row.status_label === 'On hold'
                          ? 'Blocked'
                          : row.status_label}
                </P>
                <P noDefaultMargin>
                    {summaryText(row, Boolean(overview?.run.lock.locked), month, baseCode)}
                </P>
            </div>
            {!row.current_evidence.available ? (
                <P size="small">The saved details for this result are unavailable.</P>
            ) : null}
            <dl className={Style.evidenceNumbers}>
                <div>
                    <dt>Time that counts</dt>
                    <dd>
                        {timeText(
                            row.is_current
                                ? (facts?.eligible_seconds ?? rowSeconds(row))
                                : null
                        )}
                    </dd>
                </div>
                <div>
                    <dt>Time needed for this code</dt>
                    <dd>{timeText(row.required_seconds)}</dd>
                </div>
                <div>
                    <dt>Units to bill</dt>
                    <dd>
                        {row.is_current
                            ? (row.current_payable_units ?? 'Not decided')
                            : 'Update needed'}
                    </dd>
                </div>
            </dl>
            {outsideSeconds > 0 ? (
                <P size="small">
                    {timeText(outsideSeconds)} fell outside the enrollment period and was not
                    counted.
                </P>
            ) : null}
            {uncertain.length ? (
                <section>
                    <H role="h6" noDefaultMargin>End-date care not counted</H>
                    <P size="small">
                        These work dates include an enrollment or membership end. The CSV does not
                        show whether care happened before it ended, so these minutes are not counted.
                    </P>
                    <ul className={Style.evidenceAttention}>
                        {uncertain.map((activity, index) => (
                            <li key={`${activity.work_date}:${activity.stream}:${index}`}>
                                {workDateText(activity.work_date)} · {careReasonText(activity.stream)}:{' '}
                                {timeText(activity.seconds)}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
            {facts?.excess_complete_seconds ? (
                <P size="small">
                    {timeText(facts.excess_complete_seconds)} of additional care time is recorded
                    above the billing limit.
                </P>
            ) : null}
            {otherReviews.length || attention.length ? (
                <section>
                    <H role="h6" noDefaultMargin>
                        {otherReviews.length ? 'Other checks to resolve' : 'What needs attention'}
                    </H>
                    <ul className={Style.evidenceAttention}>
                        {otherReviews.length ? otherReviews.map((kind) => (
                            <li key={kind}>{warningExplanation(row, kind, month, baseCode)}</li>
                        )) : attention.map(([key, check]) => (
                            <li key={key}>
                                <strong>{CHECK_LABELS[key] ?? careReasonText(key)}</strong>:{' '}
                                {STATE_LABELS[check.state] ?? 'Needs confirmation'}.
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
            {notes.length || row.hold_reason ? (
                <section>
                    <H role="h6" noDefaultMargin>
                        Reviewer notes
                    </H>
                    {notes.map((note) => (
                        <P key={note} size="small">{note}</P>
                    ))}
                    {row.hold_reason ? <P size="small">Block reason: {row.hold_reason}</P> : null}
                </section>
            ) : null}
            <P size="small" noDefaultMargin>
                {row.program === 'RTM'
                    ? 'Patient contact and double-counted time are not checked here. The provider must verify them.'
                    : 'The provider must check that care time is not counted twice.'}
            </P>
            <details className={Style.evidenceDetails}>
                <summary>Enrollment and conditions</summary>
                <div className={Style.evidenceDetailBody}>
                    {row.eligibility.available ? (
                        row.eligibility.lineage.map((line, index) => (
                            <dl className={Style.factList} key={index}>
                                {Object.entries(DATE_LABELS)
                                    .filter(([key]) => typeof line[key] === 'string' && line[key])
                                    .map(([key, label]) => (
                                        <React.Fragment key={key}>
                                            <dt>{label}</dt>
                                            <dd>{dateTime(line[key] as string)}</dd>
                                        </React.Fragment>
                                    ))}
                            </dl>
                        ))
                    ) : (
                        <P size="small">Enrollment dates were not recorded for this result.</P>
                    )}
                    {row.conditions.state === 'unavailable' ? (
                        <P size="small">Condition records are unavailable.</P>
                    ) : row.conditions.items.length ? (
                        <ul>
                            {row.conditions.items.map((item) => (
                                <li key={item.record_id}>
                                    {item.name ||
                                        item.icd_code ||
                                        `Condition record ${item.record_id}`}
                                    {item.name && item.icd_code ? ` (${item.icd_code})` : ''}
                                    {item.status ? ` · ${careReasonText(item.status)}` : ''}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <P size="small">No conditions recorded.</P>
                    )}
                    {overview?.run.manifest?.condition_review_enabled === false ? (
                        <P size="small">
                            Conditions are shown for reference and are not checked for billing in
                            this version.
                        </P>
                    ) : null}
                </div>
            </details>
            <details className={Style.evidenceDetails}>
                <summary>Checks and review details</summary>
                <div className={Style.evidenceDetailBody}>
                    {!row.explanation_available ? (
                        <P size="small">Detailed checks were not saved for this result.</P>
                    ) : (
                        Object.entries(row.checks).map(([key, check]) => (
                            <div key={key} className={Style.evidenceCheck}>
                                <strong>
                                    {CHECK_LABELS[key] ?? careReasonText(key)}:{' '}
                                    {STATE_LABELS[check.state] ?? careReasonText(check.state)}
                                </strong>
                                {check.actual_seconds !== undefined ? (
                                    <P size="small">
                                        Recorded: {timeText(check.actual_seconds)}. Needed:{' '}
                                        {timeText(check.required_seconds)}.
                                    </P>
                                ) : null}
                                {check.explanation ? <P size="small">{check.explanation}</P> : null}
                                {check.source || check.scope ? (
                                    <P size="small">
                                        Checked using:{' '}
                                        {SOURCE_LABELS[check.source ?? check.scope ?? ''] ??
                                            careReasonText(check.source ?? check.scope ?? null)}
                                        .
                                    </P>
                                ) : null}
                            </div>
                        ))
                    )}
                    {facts?.decision_events?.map((event) => (
                        <P size="small" key={event.event_id}>
                            {event.reason} · {actorName(event.actor)} · {dateTime(event.created_at)}
                        </P>
                    ))}
                    {Object.entries(facts?.review_proof?.warning_answers ?? {}).map(
                        ([kind, answer]) => (
                            <div key={kind}>
                                <P size="small">
                                    {REVIEW_LABELS[kind] ?? careReasonText(kind)}:{' '}
                                    {answer.outcome === 'billable'
                                        ? 'Billable'
                                        : answer.outcome === 'not_billable'
                                          ? 'Not billable'
                                          : 'Not recorded'}
                                    .
                                </P>
                                {answer.approval_reference ? (
                                    <P size="small">
                                        Approval reference: {answer.approval_reference}
                                    </P>
                                ) : null}
                                {answer.note ? <P size="small">Note: {answer.note}</P> : null}
                                {answer.condition_confirmation?.evidence_references.map(
                                    (reference, index) => (
                                        <P size="small" key={index}>
                                            Record reference:{' '}
                                            {reference.type === 'external'
                                                ? reference.reference
                                                : `Condition record ${reference.record_id}`}
                                        </P>
                                    )
                                )}
                            </div>
                        )
                    )}
                </div>
            </details>
            <details className={Style.evidenceDetails}>
                <summary>Saved record</summary>
                <div className={Style.evidenceDetailBody}>
                    <P size="small">Original billing explanation: {row.why_summary}</P>
                    <dl className={Style.factList}>
                        <dt>Condition records from</dt>
                        <dd>{careReasonText(row.conditions.source)}</dd>
                        <dt>Conditions apply to this month</dt>
                        <dd>
                            {row.conditions.historical_applicability === 'not_established'
                                ? 'Not confirmed'
                                : careReasonText(row.conditions.historical_applicability)}
                        </dd>
                        <dt>Condition requirement</dt>
                        <dd>
                            {row.conditions.criterion_state === 'not_assessed'
                                ? 'Not checked'
                                : careReasonText(row.conditions.criterion_state)}
                        </dd>
                        {facts?.raw_seconds != null ? (
                            <>
                                <dt>Time in provider CSV</dt>
                                <dd>{timeText(facts.raw_seconds)}</dd>
                            </>
                        ) : null}
                        {facts?.calculated_units != null ? (
                            <>
                                <dt>Units before reviewer decisions</dt>
                                <dd>{facts.calculated_units}</dd>
                            </>
                        ) : null}
                        {facts?.addon_cap != null ? (
                            <>
                                <dt>Maximum additional units</dt>
                                <dd>{facts.addon_cap}</dd>
                            </>
                        ) : null}
                        {facts?.cap_source ? (
                            <>
                                <dt>Billing limit source</dt>
                                <dd>{careReasonText(facts.cap_source)}</dd>
                            </>
                        ) : null}
                        {facts?.incomplete_block_seconds != null ? (
                            <>
                                <dt>Time left below the next unit</dt>
                                <dd>{timeText(facts.incomplete_block_seconds)}</dd>
                            </>
                        ) : null}
                        {Object.entries(facts?.eligible_seconds_by_stream ?? {}).map(
                            ([stream, seconds]) => (
                                <React.Fragment key={stream}>
                                    <dt>{careReasonText(stream)}</dt>
                                    <dd>{duration(seconds)} (minutes:seconds)</dd>
                                </React.Fragment>
                            )
                        )}
                        {row.current_evidence.run_id != null ? (
                            <>
                                <dt>Qualification run</dt>
                                <dd>{row.current_evidence.run_id}</dd>
                            </>
                        ) : null}
                        {(facts?.rule_version ?? overview?.rule_version) ? (
                            <>
                                <dt>Billing rule version</dt>
                                <dd>{facts?.rule_version ?? overview?.rule_version}</dd>
                            </>
                        ) : null}
                        {typeof overview?.run.audit_context?.actor === 'string' ? (
                            <>
                                <dt>Processed by</dt>
                                <dd>{overview.run.audit_context.actor}</dd>
                            </>
                        ) : null}
                        {typeof overview?.run.audit_context?.finished_at === 'string' ? (
                            <>
                                <dt>Processed on</dt>
                                <dd>{dateTime(overview.run.audit_context.finished_at)}</dd>
                            </>
                        ) : null}
                    </dl>
                    {receipts.map(([kind, receipt]) => (
                        <P key={kind} size="small">
                            {careReasonText(kind)}: import {receipt.import_id}, matched{' '}
                            {dateTime(receipt.matched_at)}, received {dateTime(receipt.received_at)}
                            .
                        </P>
                    ))}
                    {row.current_evidence.reason ? (
                        <P size="small">
                            Saved evidence: {careReasonText(row.current_evidence.reason)}.
                        </P>
                    ) : null}
                    <P size="small">
                        {overview?.run.lock.locked
                            ? 'This month is locked. These are the saved billing details.'
                            : 'If the patient’s information changes, select Calculate billable codes again.'}
                    </P>
                </div>
            </details>
        </div>
    );
};

export default EvidencePanel;
